import React from "react"
import useStyles from "../styles/Step3.styles"
import { Controller } from "react-hook-form"
import { Step3Props } from "../types"
import {
    Alert,
    CircularProgress,
    FormControl,
    FormHelperText,
    InputLabel,
    OutlinedInput,
    Stack,
    Tooltip,
    Typography,
    Box,
    TextField,
    MenuItem,
    Grid,
    Button,
    Autocomplete,
    useMediaQuery,
    Theme,
} from "@mui/material"
import { useSnackbar } from "notistack"
import { useGtmPageView } from "../../hooks"
import { ArrowForwardRounded, InfoRounded } from "@mui/icons-material"
import { AvatarPlayer } from "../../common"

const Step3View: React.FC<Step3Props> = ({
    form,
    onStepBack,
    conditions,
    options,
    ares,
    streetSugestions,
    isUpdating,
    activationObject,
    dynamicSiteData,
    dynamicDataLoading,
}) => {
    const classes = useStyles()
    const { control, formState, handleSubmit, clearErrors, watch } = form.form
    const { errors } = formState

    const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.between("xs", "md"))
    const isTablet = useMediaQuery((theme: Theme) => theme.breakpoints.between("md", "lg"))

    const isPortable = isMobile || isTablet

    const { enqueueSnackbar } = useSnackbar()
    React.useEffect(() => {
        if (options.error) enqueueSnackbar(options.error, { variant: "error" })
    }, [enqueueSnackbar, options.error])

    const city = watch("invoiceAddressCity")

    useGtmPageView({ pageName: "Step3", synonym: activationObject?.synonym })

    const isLegal = activationObject?.personType === "Legal"

    return (
        <form onSubmit={handleSubmit(form.onSubmit)} method="post" autoComplete="off">
            <Grid container p={2}>
                <Grid item xs={12} lg={7} spacing={3}>
                    <Box className="pb-8 md:pb-14">
                        <Typography className="font-bold text-2xl pb-4 md:text-4xl md:pb-8" variant="h1">
                            Fakturační údaje
                        </Typography>
                        <Typography variant="body1" paragraph className="font-thin">
                            Vyplňte a zkontrolujte Vaše fakturační údaje.
                        </Typography>
                        {isPortable && <AvatarPlayer className="lg:hidden" videoName="Invoicing.mp4" />}
                    </Box>
                    <Grid container spacing={{ xs: 2, md: 3 }}>
                        {isLegal && (
                            <>
                                <Grid item xs={6}>
                                    <Controller
                                        name={"ico"}
                                        control={control}
                                        render={({ field }) => (
                                            <TextField
                                                fullWidth
                                                value={ares.icoSearch}
                                                onChange={(e) => {
                                                    ares.handleSetIcoSearch(e.target.value)
                                                    field.onChange(e.target.value)
                                                    clearErrors("ico")
                                                }}
                                                className="rounded-md"
                                                disabled={ares.isLoading}
                                                error={errors.ico !== undefined}
                                                helperText={errors.ico?.message}
                                                variant="outlined"
                                                InputProps={{
                                                    endAdornment: ares.isLoading && <CircularProgress size={15} />,
                                                }}
                                                label="IČ"
                                            />
                                        )}
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <Controller
                                        name={"vat"}
                                        control={control}
                                        render={({ field }) => (
                                            <TextField
                                                fullWidth
                                                {...field}
                                                disabled={ares.isLoading || isLegal}
                                                error={errors.vat !== undefined}
                                                helperText={errors.vat?.message}
                                                className="rounded-md"
                                                variant="outlined"
                                                label="DIČ"
                                            />
                                        )}
                                    />
                                </Grid>
                                {ares.notFound && (
                                    <Grid item xs={12}>
                                        <Alert severity="error">Nenalezna odpovídající společnost v registru ARES</Alert>
                                    </Grid>
                                )}
                            </>
                        )}
                        <Grid item xs={12}>
                            <Controller
                                name={"invoiceAddressName"}
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        fullWidth
                                        label={conditions.personType === "Legal" ? "Název společnosti" : "Jméno a příjmení"}
                                        {...field}
                                        className="rounded-md"
                                        disabled={ares.isLoading || isLegal}
                                        error={errors.invoiceAddressName !== undefined}
                                        helperText={errors.invoiceAddressName?.message}
                                        variant="outlined"
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={5} md={3}>
                            <Controller
                                name={"invoiceAddressZIP"}
                                control={control}
                                render={({ field }) => (
                                    <Autocomplete
                                        options={options.zipCodes.map((option) => option.label)}
                                        {...field}
                                        defaultValue=""
                                        onChange={(_event, newValue) => {
                                            field.onChange(newValue || "")
                                        }}
                                        filterOptions={(options, state) => {
                                            const trimmed = state.inputValue.replace(" ", "")
                                            return options.filter((opt) => opt.startsWith(trimmed))
                                        }}
                                        noOptionsText="žádné shody"
                                        className={classes.zipAutocomplete}
                                        disabled={ares.isLoading || isLegal}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                type="text"
                                                value={options.zipCodeSearch}
                                                className="rounded-md"
                                                onChange={(e) => {
                                                    options.handleZipCodeSearchChanged(e.target.value)
                                                }}
                                                error={errors.invoiceAddressZIP !== undefined}
                                                helperText={errors.invoiceAddressZIP?.message}
                                                label="PSČ"
                                            />
                                        )}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={7} sm={4.5}>
                            <Controller
                                name={"invoiceAddressCity"}
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        fullWidth
                                        select={options.cities !== undefined}
                                        disabled={options.cities === undefined || ares.isLoading || isLegal}
                                        className="rounded-md"
                                        error={errors.invoiceAddressCity !== undefined}
                                        helperText={errors.invoiceAddressCity?.message}
                                        label="Město"
                                        SelectProps={{
                                            renderValue: () => city,
                                        }}
                                    >
                                        {options.cities &&
                                            options.cities.map((option, index) => (
                                                <MenuItem key={index} value={option.name}>
                                                    {option.name}
                                                </MenuItem>
                                            ))}
                                    </TextField>
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} md={4.5}>
                            <Controller
                                name={"invoiceAddressStreet"}
                                control={control}
                                render={({ field }) => (
                                    <Autocomplete
                                        options={streetSugestions.streets.map((option) => option)}
                                        {...field}
                                        disabled={isLegal}
                                        onChange={(_event, newValue) => {
                                            field.onChange(newValue || "")
                                        }}
                                        disableClearable
                                        noOptionsText={!streetSugestions.isLoading ? "žádné shody" : undefined}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                type="text"
                                                value={streetSugestions.streetSearch}
                                                onChange={(e) => {
                                                    streetSugestions.handleStreetSearchChanged(e.target.value)
                                                }}
                                                className="rounded-md"
                                                error={errors.invoiceAddressStreet !== undefined}
                                                helperText={errors.invoiceAddressStreet?.message}
                                                label="Ulice"
                                            />
                                        )}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={6} md={3}>
                            <Controller
                                name={"invoiceAddressCodeNumber"}
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        disabled={isLegal}
                                        error={errors.invoiceAddressCodeNumber !== undefined}
                                        className="rounded-md"
                                        helperText={errors.invoiceAddressCodeNumber?.message}
                                        fullWidth
                                        label="Číslo popisné"
                                        variant="outlined"
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={6} md={3}>
                            <Controller
                                name={"invoiceAddressGuidanceNumber"}
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        disabled={isLegal}
                                        error={errors.invoiceAddressGuidanceNumber !== undefined}
                                        className="rounded-md"
                                        helperText={errors.invoiceAddressGuidanceNumber?.message}
                                        fullWidth
                                        label="Číslo orientační"
                                        variant="outlined"
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <FormControl variant="outlined" fullWidth>
                                <InputLabel htmlFor="emailInvoice">Fakturační e-mail</InputLabel>
                                <Controller
                                    name={"emailInvoice"}
                                    control={control}
                                    render={({ field }) => (
                                        <OutlinedInput
                                            id="emailInvoice"
                                            fullWidth
                                            {...field}
                                            disabled={isLegal}
                                            error={errors.emailInvoice !== undefined}
                                            className="rounded-md"
                                            endAdornment={
                                                <Tooltip
                                                    title="Můžete použít maximálně 3 emailové adresy oddělené středníkem"
                                                    arrow
                                                    componentsProps={{
                                                        tooltip: {
                                                            className: "bg-pantone-gray-2 text-pantone-white-1 text-[0.875rem]",
                                                        },
                                                    }}
                                                >
                                                    <InfoRounded className="text-pantone-gray-2 cursor-pointer" />
                                                </Tooltip>
                                            }
                                            label="Fakturační e-mail"
                                        />
                                    )}
                                />
                                {errors.emailInvoice?.message && <FormHelperText id="emailInvoice">{errors.emailInvoice?.message}</FormHelperText>}
                            </FormControl>
                        </Grid>
                        {activationObject?.personType === "Legal" && (
                            <Grid item xs={12}>
                                <Alert severity="info" sx={{ marginBottom: 1 }} className="rounded-md text-sm md:text-base">
                                    Pro změnu fakturačního emailu kontaktujte naše Zákaznické centrum na telefonním čísle 732 222 555 volba č.3 nebo
                                    na emailové adrese zakaznicka.linka@jablotron.cz
                                </Alert>
                            </Grid>
                        )}
                    </Grid>
                    <Stack direction="row" justifyContent="flex-end" className="mt-14 w-full" spacing={2}>
                        <Button
                            variant="contained"
                            color="primary"
                            type="submit"
                            size="large"
                            className="normal-case text-[0.9rem] font-semibold lg:font-normal lg:text-xl shadow-none rounded-md"
                            disabled={isUpdating}
                            endIcon={isUpdating ? <CircularProgress size={20} /> : <ArrowForwardRounded className="text-2xl" />}
                        >
                            potvrdit
                        </Button>
                    </Stack>
                </Grid>
                <Grid item xs={0} lg={1} className="hidden lg:block"></Grid>
                <Grid item xs={0} lg={4} className="hidden lg:block">
                    {dynamicDataLoading ? "Načítám avatara..." : !isPortable ? <AvatarPlayer videoName="Invoicing.mp4" /> : null}
                </Grid>
            </Grid>
        </form>
    )
}

export default Step3View
